// ControlPanel component - handles algorithm selection, dataset, and controls
import { useState } from 'react';
import { DatasetGenerator } from '@/core/DatasetGenerator';
import { Algorithms } from '@/algorithms';
import type { SortAlgorithm } from '@/core/types';
import { Colors } from '@/styles/colors';
import { FiPlay, FiPause, FiSkipBack, FiSkipForward, FiRotateCw } from 'react-icons/fi';
import { formatCategoryLabel, formatDatasetLabel } from './copy';

interface ControlPanelProps {
  algorithm: SortAlgorithm;
  dataset: string;
  speed: number;
  isPlaying: boolean;
  onAlgorithmChange: (algorithmName: string) => void;
  onDatasetChange: (datasetName: string) => void;
  onSpeedChange: (speed: number) => void;
  onPlayPause: () => void;
  onStepForward: () => void;
  onStepBackward: () => void;
  onReset: () => void;
}

export function ControlPanel({
  algorithm,
  dataset,
  speed,
  isPlaying,
  onAlgorithmChange,
  onDatasetChange,
  onSpeedChange,
  onPlayPause,
  onStepForward,
  onStepBackward,
  onReset
}: ControlPanelProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const presetNames = DatasetGenerator.getPresetNames();

  return (
    <div className="control-panel" style={{ backgroundColor: Colors.light.surface, border: `1px solid ${Colors.light.border}`, borderRadius: '8px', padding: '20px', flex: '1', minWidth: 0 }}>
      <h2 style={{ margin: '0 0 20px 0', color: Colors.light.primary }}>Kontrol belajar</h2>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', color: Colors.light.textSecondary, fontSize: '14px' }}>Algoritma</label>
        <select
          value={algorithm.name}
          onChange={(e) => onAlgorithmChange(e.target.value)}
          style={{ width: '100%', padding: '10px', backgroundColor: Colors.light.background, border: `1px solid ${Colors.light.border}`, borderRadius: '4px', color: Colors.light.text }}
        >
          {Algorithms.map((algo) => (
            <option key={algo.name} value={algo.name}>{algo.name}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', color: Colors.light.textSecondary, fontSize: '14px' }}>Jenis data</label>
        <select
          value={dataset}
          onChange={(e) => onDatasetChange(e.target.value)}
          style={{ width: '100%', padding: '10px', backgroundColor: Colors.light.background, border: `1px solid ${Colors.light.border}`, borderRadius: '4px', color: Colors.light.text }}
        >
          {presetNames.map((name) => (
            <option key={name} value={name}>{formatDatasetLabel(name)}</option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', color: Colors.light.textSecondary, fontSize: '14px' }}>Kecepatan: {speed}x</label>
        <input
          type="range"
          min="0.1"
          max="3"
          step="0.1"
          value={speed}
          onChange={(e) => onSpeedChange(parseFloat(e.target.value))}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '10px', marginBottom: '20px' }}>
        <button
          onClick={onStepBackward}
          aria-label="Mundur satu langkah"
          style={{ minHeight: 42, padding: '8px', backgroundColor: Colors.light.primary, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
        >
          <FiSkipBack size={16} />
        </button>
        <button
          onClick={onPlayPause}
          style={{ minHeight: 42, padding: '8px', backgroundColor: isPlaying ? Colors.light.error : Colors.light.success, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
        >
          {isPlaying ? <FiPause size={16} /> : <FiPlay size={16} />}
          {isPlaying ? 'Jeda' : 'Mulai'}
        </button>
        <button
          onClick={onStepForward}
          aria-label="Maju satu langkah"
          style={{ minHeight: 42, padding: '8px', backgroundColor: Colors.light.primary, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
        >
          <FiSkipForward size={16} />
        </button>
      </div>

      <button
        onClick={onReset}
        style={{ width: '100%', minHeight: 42, padding: '10px', backgroundColor: Colors.light.background, color: Colors.light.text, border: `1px solid ${Colors.light.border}`, borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
      >
        <FiRotateCw size={14} /> Ulangi dari awal
      </button>

      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        style={{ width: '100%', marginTop: '10px', minHeight: 40, padding: '8px', backgroundColor: 'transparent', color: Colors.light.textSecondary, border: `1px solid ${Colors.light.border}`, borderRadius: '4px', cursor: 'pointer', textAlign: 'left' }}
      >
        {showAdvanced ? 'Sembunyikan' : 'Tampilkan'} detail teknis
      </button>

      {showAdvanced && (
        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: Colors.light.background, borderRadius: '4px', fontSize: '12px', color: Colors.light.textSecondary }}>
          <div style={{ marginBottom: '5px' }}>
            <strong>Kategori:</strong> {formatCategoryLabel(algorithm.category)}
          </div>
          <div style={{ marginBottom: '5px' }}>
            <strong>Stabil:</strong> {algorithm.stable ? 'Ya' : 'Tidak'}
          </div>
          <div style={{ marginBottom: '5px' }}>
            <strong>In-place:</strong> {algorithm.inPlace ? 'Ya' : 'Tidak'}
          </div>
          <div style={{ marginBottom: '5px' }}>
            <strong>Adaptif:</strong> {algorithm.adaptive ? 'Ya' : 'Tidak'}
          </div>
        </div>
      )}
    </div>
  );
}
